import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AssigningRoundEnum, OrderRiderActionEnum } from '@shared/enums';
import {
    GetRiderAssigningHistoryRequestDto,
    RiderAssigningHistory,
    RiderAssigningItem
} from '@shared/models';
import { SharedDeliveryOrderService } from '@shared/services/shared-delivery-order.service';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';

@Component({
	selector: 'booking-assigning-tab',
	templateUrl: './booking-assigning-tab.component.html',
	styleUrl: './booking-assigning-tab.component.scss'
})
export class BookingAssigningTabComponent implements OnChanges, OnDestroy {
	@Input() orderId: number = 0;
	@Input() isActive: boolean = false;

	loading: boolean = false;
	assigningData: RiderAssigningHistory | null = null;

	round1Riders: RiderAssigningItem[] = [];
	round2Riders: RiderAssigningItem[] = [];
	round3Riders: RiderAssigningItem[] = [];

	// Current assignment tracking
	currentRider: RiderAssigningItem | null = null;
	currentRiderIndex: number | undefined = undefined;
	currentRound: AssigningRoundEnum = AssigningRoundEnum.None;

	// Progress animation
	progressValue: number = 20;
	targetValue: number = 0;
	duration: number = 20000;
	private animationStartTime: number = 0;
	private animationIntervalId: any = null;

	// Expansion panel state
	round1Expanded: boolean = false;
	round2Expanded: boolean = false;
	round3Expanded: boolean = false;

	// Enums for template
	AssigningRoundEnum = AssigningRoundEnum;
	OrderRiderActionEnum = OrderRiderActionEnum;

	constructor(
		private deliveryOrderService: SharedDeliveryOrderService,
		private messageService: MessageService
	) { }

	ngOnChanges(changes: SimpleChanges): void {
		// Load data when tab becomes active
		if (changes['isActive'] && this.isActive && !this.assigningData) {
			this.loadAssigningData();
		}
	}

	async loadAssigningData() {
		if (!this.orderId || this.orderId <= 0) {
			return;
		}

		this.loading = true;
		try {
			const dto: GetRiderAssigningHistoryRequestDto = {
				OrderId: this.orderId,
				TimeZoneOffset: Utils.getTimezoneOffset()
			};

			this.assigningData = await this.deliveryOrderService.getRiderAssigningHistory(dto, this.messageService);

			if (!this.assigningData?.Success) {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: this.assigningData?.ErrorMessage || 'Failed to load assigning data'
				});
				this.loading = false;
				return;
			}

			this.processAssigningData();
		} catch (err) {
			console.error('Error loading assigning data:', err);
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load assigning data'
			});
		} finally {
			this.loading = false;
		}
	}

	private processAssigningData() {
		if (!this.assigningData) {return;}

		// Split riders by round
		this.round1Riders = this.assigningData.RiderAssigningList.filter(
			(x) => x.RoundNumber === AssigningRoundEnum.First
		);
		this.round2Riders = this.assigningData.RiderAssigningList.filter(
			(x) => x.RoundNumber === AssigningRoundEnum.Second
		);
		this.round3Riders = this.assigningData.RiderAssigningList.filter(
			(x) => x.RoundNumber === AssigningRoundEnum.Third
		);

		// Set current assignment
		this.setCurrentAssigningData();

		// Start progress animation if needed (always show for demo with dummy data)
		if (this.currentRider) {
			this.progressValue = 20;
			this.animateProgress();
		}
	}

	private setCurrentAssigningData() {
		if (!this.checkRound(this.round1Riders)) {
			if (!this.checkRound(this.round2Riders)) {
				if (!this.checkRound(this.round3Riders)) {
					this.currentRound = AssigningRoundEnum.None;
				}
			}
		}
	}

	private checkRound(riders: RiderAssigningItem[]): boolean {
		const riderIndex = riders.findIndex((item) => item.RiderAction === OrderRiderActionEnum.Offer);
		const rider = riders.find((item) => item.RiderAction === OrderRiderActionEnum.Offer);

		if (riderIndex !== -1 && rider) {
			this.currentRiderIndex = riderIndex;
			this.currentRider = rider;
			this.currentRound = rider.RoundNumber;
			this.setExpandedRound(this.currentRound);
			return true;
		}

		return false;
	}

	private setExpandedRound(round: AssigningRoundEnum) {
		this.round1Expanded = round === AssigningRoundEnum.First;
		this.round2Expanded = round === AssigningRoundEnum.Second;
		this.round3Expanded = round === AssigningRoundEnum.Third;
	}

	private animateProgress() {
		if (this.animationIntervalId) {
			clearInterval(this.animationIntervalId);
		}

		this.animationStartTime = Date.now();
		const startValue = 20;
		const endValue = 0;

		this.animationIntervalId = setInterval(() => {
			const elapsed = Date.now() - this.animationStartTime;
			const totalDuration = this.duration;

			if (elapsed >= totalDuration) {
				this.progressValue = endValue;
				clearInterval(this.animationIntervalId);
				this.animationIntervalId = null;
			} else {
				const progress = elapsed / totalDuration; // 0 to 1
				this.progressValue = startValue + (endValue - startValue) * progress;
				this.progressValue = Math.max(endValue, Math.min(startValue, this.progressValue));
			}
		}, 100);
	}

	private async moveToNextRider() {
		if (this.currentRider) {
			this.currentRider.RiderAction = OrderRiderActionEnum.TimedOut;
			this.currentRider.RiderActionText = 'Timed Out';
		}

		const currentRoundRiders = this.getCurrentRoundRiders();
		if (this.currentRiderIndex !== undefined && currentRoundRiders) {
			const nextIndex = this.currentRiderIndex + 1;
			if (nextIndex < currentRoundRiders.length) {
				this.currentRiderIndex = nextIndex;
				this.currentRider = currentRoundRiders[nextIndex];
				this.progressValue = 20;
				this.animateProgress();
				return;
			}
		}

		if (this.currentRound === AssigningRoundEnum.First && this.round2Riders.length > 0) {
			this.currentRound = AssigningRoundEnum.Second;
			this.currentRiderIndex = 0;
			this.currentRider = this.round2Riders[0];
			this.setExpandedRound(this.currentRound);
			this.progressValue = 20;
			this.animateProgress();
		} else if (this.currentRound === AssigningRoundEnum.Second && this.round3Riders.length > 0) {
			this.currentRound = AssigningRoundEnum.Third;
			this.currentRiderIndex = 0;
			this.currentRider = this.round3Riders[0];
			this.setExpandedRound(this.currentRound);
			this.progressValue = 20;
			this.animateProgress();
		} else {
			this.currentRound = AssigningRoundEnum.None;
			this.currentRiderIndex = undefined;
			this.currentRider = null;
			console.log('All riders exhausted - order expired');
		}
	}

	private getCurrentRoundRiders(): RiderAssigningItem[] | null {
		switch (this.currentRound) {
			case AssigningRoundEnum.First:
				return this.round1Riders;
			case AssigningRoundEnum.Second:
				return this.round2Riders;
			case AssigningRoundEnum.Third:
				return this.round3Riders;
			default:
				return null;
		}
	}

	isRiderActive(item: RiderAssigningItem, index: number, round: AssigningRoundEnum): boolean {
		if (this.currentRound !== round) {return false;}
		return index === this.currentRiderIndex;
	}

	shouldShowRider(item: RiderAssigningItem, index: number, round: AssigningRoundEnum): boolean {
		if (this.currentRound < round) {return false;}
		if (this.currentRound > round) {return true;}
		if (this.currentRound === round) {
			return item.RiderAction === OrderRiderActionEnum.Offer;
		}
		return true;
	}

	isRiderDisabled(item: RiderAssigningItem, index: number, round: AssigningRoundEnum): boolean {
		if (this.currentRound < round) {return true;}
		if (this.currentRound > round) {return item.RiderAction !== OrderRiderActionEnum.Accept;}
		if (this.currentRound === round && this.currentRiderIndex !== undefined) {
			return index < this.currentRiderIndex;
		}
		return false;
	}

	getRiderClass(item: RiderAssigningItem, index: number, round: AssigningRoundEnum): string {
		if (item.RiderAction === OrderRiderActionEnum.Accept) {
			return 'rider-item-accepted';
		}
		if (this.isRiderActive(item, index, round)) {
			return 'rider-item-active';
		}
		if (this.isRiderDisabled(item, index, round)) {
			return 'rider-item-disabled';
		}
		return 'rider-item-pending';
	}

	ngOnDestroy() {
		// Proper cleanup to prevent memory leaks
		if (this.animationIntervalId) {
			clearInterval(this.animationIntervalId);
			this.animationIntervalId = null;
		}

		// Clear data
		this.assigningData = null;
	}
}
